import {
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Directive,
  ViewChild,
  ChangeDetectorRef,
  inject, RendererFactory2
} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject} from 'rxjs';
import * as _ from 'lodash';
import {PlatformLocation} from '@angular/common';
import {filter, map, switchMap, takeUntil} from 'rxjs/operators';
import {GLOBAL, IdCodeNameQuery, LeModalService, simplifiedRegexSearch} from 'common-shared';
import {NgSelectComponent} from "@ng-select/ng-select";
import {ComponentCanDeactivate} from "../services/pending-changes.guard";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";

@Directive()
export class EditComponent implements OnDestroy {
  @Output() changed = new EventEmitter<boolean>();

  protected componentDestroyed: Subject<void> = new Subject();
  _ = _;

  constructor() {
    this._ = _;
  }

  ngOnDestroy(): void {
    this.componentDestroyed.next();
    this.componentDestroyed.unsubscribe();
  }

  trackById = (index: number, item: {Id: string}) => item?.Id;
}

@Directive()
export class SelectComponent<T extends {Id:string}> implements OnInit, OnDestroy {
  private _items: T[] = [];
  @Input()
  set items(value: T[]) {
    this._items = value;
    this.refreshToken$.next(undefined);
  }
  get items() {
    return this._items;
  }
  protected componentDestroyed: Subject<void> = new Subject();
  _ = _;

  @Input() items$: Observable<T[]>;
  @Input() entityId: string;
  @Output() entityIdChange = new EventEmitter<string>();
  @Output() entityChange = new EventEmitter<T>();
  @Input() entityIds: string[];
  @Output() entityIdsChange = new EventEmitter();
  @Input() multiple = false;
  @Input() name: string;
  @Input() readonly = false;
  @Input() allowAdd = true;
  @Input() allowEdit = true;
  @Input() clearable = true;
  @Input() appendTo: string = 'body';
  @Input() placeholder: string;
  @Input('disabled') disabled = false;
  @Input() initOpened = false;
  @ViewChild(NgSelectComponent, {static: false}) select: NgSelectComponent;

  refreshToken$ = new BehaviorSubject(undefined);
  allItems$: Observable<T[]> = this.refreshToken$.pipe(switchMap(() => this.items$ || of(this._items ?? [])), takeUntil(this.componentDestroyed), map(value => {
    this.loading = false;
    return value;
  }));
  Classificators = GLOBAL.Classificators;
  loading = true;
  protected changeDetectorRef = inject(ChangeDetectorRef);
  protected modalService = inject(LeModalService);
  protected location = inject(PlatformLocation);

  constructor() {
    this._ = _;

    this.location.onPopState(() => {
      return this.modalService.dismissAll('back');
    });
  }

  ngOnInit(): void {
    this.refreshToken$.next(undefined);
  }

  ngAfterViewInit(): void {
    if (this.initOpened) {
      setTimeout(() => {
        this.select.open();
        this.changeDetectorRef.detectChanges();
      });
    }
  }

  protected setEntity(entity: T) {
    this.entityChange.emit(entity);
    this.setEntityId(entity?.Id);
  }

  public setEntityId(entityId: string) {
    this.entityId = entityId;
    this.entityIdChange.emit(this.entityId);
    this.refreshToken$.next(undefined);
  }

  ngOnDestroy(): void {
    this.componentDestroyed.next();
    this.componentDestroyed.unsubscribe();
  }

  searchFn(term: string, item: IdCodeNameQuery): boolean {
    return simplifiedRegexSearch(term, [item.Code, item.Name, item.DisplayName]);
  }
}

@Directive()
export class EditStateComponent implements OnDestroy, ComponentCanDeactivate {
  protected componentDestroyed: Subject<void> = new Subject();
  _ = _;
  public saveState$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public deleteState$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  protected isForceSaveShown$ = new BehaviorSubject<boolean>(false);
  public shouldResetSaveState$ = new Subject<any>();
  public validate$: Subject<boolean> = new Subject<boolean>();
  public readonly: boolean = false;
  isNew: boolean = false;
  protected router = inject(Router);
  private rendererFactory2 = inject(RendererFactory2);
  protected activatedRoute = inject(ActivatedRoute);
  private unlistenFn: () => void;
  pendingChangesDisabled: boolean;

  constructor() {
    this.router.events.pipe(takeUntil(this.componentDestroyed),
      filter(value => value instanceof NavigationEnd)
    ).subscribe(res => {
      this.deleteState$.next(true);
      this.isForceSaveShown$.next(false);
      if (!this.router.getCurrentNavigation().extras?.state?.skipReset) {
        this.shouldResetSaveState$.next(true);
        this.triggerChanges();
      }
      this.isForceSaveShown$.next(false);
    });
    this.pendingChangesDisabled = (localStorage.getItem('pending-changes-disabled') ?? 'false') === 'true';
    if (!this.pendingChangesDisabled && this.activatedRoute.routeConfig.data?.beforeUnloadLogic) {
      this.listenUnloadEvent();
    }
  }

  ngOnDestroy(): void {
    if (this.unlistenFn) {
      this.unlistenFn();
    }
    this.componentDestroyed.next();
    this.componentDestroyed.unsubscribe();
  }

  triggerChanges(hasChanges: boolean = false) {
    this.saveState$.next(hasChanges);
  }

  canDeactivate(): boolean {
    return !this.saveState$.value;
  }

  private listenUnloadEvent() {
    const renderer = this.rendererFactory2.createRenderer(null, null);
    this.unlistenFn = renderer.listen('window', 'beforeunload', (evt) => {
      if (!this.canDeactivate()) {
        evt.returnValue = '';
        return false;
      }
    });
  }
}
